/**
 * Plumbing subservice flow: problem selection, branch steps, and tail steps.
 * Kept to display saved requests from the earlier plumbing wizard.
 */
import BuildIcon from '@mui/icons-material/Build';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CountertopsIcon from '@mui/icons-material/Countertops';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KitchenIcon from '@mui/icons-material/Kitchen';
import ShowerIcon from '@mui/icons-material/Shower';
import WcIcon from '@mui/icons-material/Wc';
import FoundationIcon from '@mui/icons-material/Foundation';
import HomeIcon from '@mui/icons-material/Home';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import BlockIcon from '@mui/icons-material/Block';
import BoltIcon from '@mui/icons-material/Bolt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import HandymanIcon from '@mui/icons-material/Handyman';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SearchIcon from '@mui/icons-material/Search';
import InvertColorsOffIcon from '@mui/icons-material/InvertColorsOff';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import WaterIcon from '@mui/icons-material/Water';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import ConstructionIcon from '@mui/icons-material/Construction';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import BathtubIcon from '@mui/icons-material/Bathtub';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import YardIcon from '@mui/icons-material/Yard';
import WaterDamageIcon from '@mui/icons-material/WaterDamage';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import CheckIcon from '@mui/icons-material/Check';
import SensorsIcon from '@mui/icons-material/Sensors';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import PendingIcon from '@mui/icons-material/Pending';
import LayersIcon from '@mui/icons-material/Layers';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TimerIcon from '@mui/icons-material/Timer';
import HistoryIcon from '@mui/icons-material/History';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import FloodIcon from '@mui/icons-material/Flood';
import SevereColdIcon from '@mui/icons-material/SevereCold';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlumbingIcon from '@mui/icons-material/Plumbing';

// ─── Step 1: Problem select ────────────────────────────────────────────────
const PLUMBING_PROBLEMS = [
  { id: 'clogged-drain', label: 'Drain or Sink Is Clogged', icon: OpacityIcon },
  { id: 'leaking-pipe', label: 'Pipe or Faucet Is Leaking', icon: WaterDropIcon },
  { id: 'toilet-issue', label: 'Toilet Is Running, Leaking, or Not Flushing', icon: BuildIcon },
  { id: 'water-heater', label: 'Water Heater Help', icon: WhatshotIcon },
  { id: 'faucet-install', label: 'Install or Replace Faucet / Sink', icon: CountertopsIcon },
  { id: 'garbage-disposal', label: 'Garbage Disposal Not Working', icon: DeleteOutlineIcon },
  { id: 'urgent-plumbing', label: 'Urgent Plumbing Problem (Water Leak / Flooding)', icon: ReportProblemIcon, urgent: true },
  { id: 'not-sure', label: 'Not sure — I need a plumber to check the issue', icon: HelpOutlineIcon },
];

const URGENCY_OPTIONS = [
  { id: 'asap', label: 'As soon as possible', icon: WarningAmberIcon, urgent: true },
  { id: 'today', label: 'Today', icon: TodayIcon },
  { id: 'few-days', label: 'Within the next few days', icon: DateRangeIcon },
  { id: 'just-quotes', label: 'Just getting quotes', icon: RequestQuoteIcon },
];

const DRAIN_LOCATION_OPTIONS = [
  { id: 'kitchen-sink', label: 'Kitchen sink', icon: KitchenIcon },
  { id: 'bathroom-sink', label: 'Bathroom sink', icon: OpacityIcon },
  { id: 'shower-tub', label: 'Shower or bathtub drain', icon: ShowerIcon },
  { id: 'toilet', label: 'Toilet', icon: WcIcon },
  { id: 'floor-drain', label: 'Floor drain (basement / laundry)', icon: FoundationIcon },
  { id: 'main-drain', label: 'Main house drain / multiple drains', icon: HomeIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const DRAIN_SYMPTOM_OPTIONS = [
  { id: 'slow-drain', label: 'Water draining very slowly', icon: SpeedIcon },
  { id: 'no-drain', label: 'Water not draining at all', icon: BlockIcon },
  { id: 'backing-up', label: 'Water backing up into sink / tub', icon: WarningAmberIcon },
  { id: 'bad-smell', label: 'Bad smell coming from drain', icon: ReportProblemIcon },
  { id: 'multiple-clogged', label: 'Multiple drains are clogged', icon: PlumbingIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const TOILET_SYMPTOM_OPTIONS = [
  { id: 'running', label: 'Running constantly', icon: SpeedIcon },
  { id: 'leaking-base', label: 'Leaking at the base', icon: WaterDropIcon },
  { id: 'leaking-tank', label: 'Leaking from the tank', icon: OpacityIcon },
  { id: 'wont-flush', label: "Won't flush or weak flush", icon: BlockIcon },
  { id: 'overflowing', label: 'Overflowing when flushed', icon: WarningAmberIcon, urgent: true },
  { id: 'strange-noises', label: 'Making strange noises', icon: VolumeUpIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const TOILET_COUNT_OPTIONS = [
  { id: 'one', label: 'Just one toilet', icon: WcIcon },
  { id: 'multiple', label: 'More than one', icon: FormatListNumberedIcon },
  { id: 'all', label: 'All of them', icon: PlaylistAddCheckIcon },
];

const TOILET_TRIED_OPTIONS = [
  { id: 'tried-fix', label: 'Yes, I tried fixing it myself', icon: HandymanIcon },
  { id: 'no-try', label: "No, I haven't tried anything", icon: SentimentNeutralIcon },
  { id: 'not-comfortable', label: "I'm not comfortable trying", icon: HelpOutlineIcon },
];

const WH_NEED_OPTIONS = [
  { id: 'repair', label: "My water heater isn't working right", icon: BuildIcon },
  { id: 'install', label: 'I want to install or replace a water heater', icon: SwapHorizIcon },
  { id: 'not-sure', label: 'Not sure — I need a professional opinion', icon: HelpOutlineIcon },
];

const WH_REPAIR_SYMPTOM_OPTIONS = [
  { id: 'no-hot-water', label: 'No hot water at all', icon: InvertColorsOffIcon },
  { id: 'not-hot-enough', label: 'Water not getting hot enough', icon: ThermostatIcon },
  { id: 'leaking', label: 'Water heater is leaking', icon: WaterDropIcon },
  { id: 'noises', label: 'Strange noises from the unit', icon: VolumeUpIcon },
  { id: 'pilot-light', label: 'Pilot light keeps going out', icon: LocalFireDepartmentIcon },
  { id: 'rusty-water', label: 'Rusty or discolored water', icon: WaterIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const WH_TYPE_OPTIONS = [
  { id: 'tank-gas', label: 'Traditional tank (gas)', icon: LocalFireDepartmentIcon },
  { id: 'tank-electric', label: 'Traditional tank (electric)', icon: ElectricalServicesIcon },
  { id: 'tankless', label: 'Tankless / on-demand', icon: BoltIcon },
  { id: 'heat-pump', label: 'Heat pump / hybrid', icon: EnergySavingsLeafIcon },
  { id: 'not-sure', label: 'Not sure — need a recommendation', icon: HelpOutlineIcon },
];

const WH_REASON_OPTIONS = [
  { id: 'failing', label: 'Current water heater is failing or broken', icon: WarningAmberIcon },
  { id: 'upgrade', label: 'Current unit is old and I want to upgrade', icon: UpgradeIcon },
  { id: 'remodel', label: 'Remodeling or new construction', icon: ConstructionIcon },
  { id: 'switch-fuel', label: 'Want to switch fuel types (gas ↔ electric)', icon: SwapHorizIcon },
  { id: 'exploring', label: 'Just exploring options and pricing', icon: SearchIcon },
];

const LEAK_LOCATION_OPTIONS = [
  { id: 'kitchen-faucet', label: 'Kitchen faucet', icon: KitchenIcon },
  { id: 'bathroom-faucet', label: 'Bathroom faucet', icon: BathtubIcon },
  { id: 'pipe-under-sink', label: 'Pipe under a sink', icon: PlumbingIcon },
  { id: 'pipe-wall-ceiling', label: 'Pipe in wall or ceiling', icon: MeetingRoomIcon },
  { id: 'outdoor-faucet', label: 'Outdoor faucet / hose bib', icon: YardIcon },
  { id: 'supply-line', label: 'Water supply line', icon: WaterIcon },
  { id: 'multiple', label: 'Multiple places are leaking', icon: WaterDamageIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const LEAK_SEVERITY_OPTIONS = [
  { id: 'slow-drip', label: 'Slow drip', icon: WaterDropIcon },
  { id: 'steady-leak', label: 'Steady leak', icon: OpacityIcon },
  { id: 'spraying', label: 'Spraying or gushing water', icon: WarningAmberIcon, urgent: true },
  { id: 'only-when-running', label: 'Only leaks when water is running', icon: SensorsIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const LEAK_SHUTOFF_OPTIONS = [
  { id: 'yes', label: 'Yes, I turned off the water', icon: CheckIcon },
  { id: 'no-dont-know', label: "No, I don't know how", icon: HelpOutlineIcon },
  { id: 'no-not-bad', label: "No, it's not that bad", icon: DoNotDisturbIcon },
];

const FAUCET_NEED_OPTIONS = [
  { id: 'install-faucet', label: 'Install a new faucet', icon: CountertopsIcon },
  { id: 'replace-faucet', label: 'Replace an existing faucet', icon: SwapHorizIcon },
  { id: 'install-sink', label: 'Install a new sink', icon: CountertopsIcon },
  { id: 'replace-sink', label: 'Replace an existing sink', icon: SwapHorizIcon },
  { id: 'both', label: 'Both — faucet and sink', icon: LayersIcon },
  { id: 'not-sure', label: 'Not sure — I need a professional opinion', icon: HelpOutlineIcon },
];

const FAUCET_WHERE_OPTIONS = [
  { id: 'kitchen', label: 'Kitchen', icon: KitchenIcon },
  { id: 'bathroom', label: 'Bathroom', icon: BathtubIcon },
  { id: 'laundry', label: 'Laundry / utility room', icon: LocalLaundryServiceIcon },
  { id: 'outdoor', label: 'Outdoor', icon: YardIcon },
  { id: 'multiple', label: 'Multiple locations', icon: FormatListNumberedIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const FAUCET_PURCHASED_OPTIONS = [
  { id: 'yes', label: 'Yes, I already purchased them', icon: ShoppingCartIcon },
  { id: 'need-help', label: 'No, I need help choosing', icon: HelpCenterIcon },
  { id: 'deciding', label: "I'm still deciding", icon: PendingIcon },
];

const DISPOSAL_SYMPTOM_OPTIONS = [
  { id: 'wont-turn-on', label: "Disposal won't turn on", icon: PowerOffIcon },
  { id: 'jammed', label: 'Disposal is jammed / stuck', icon: BlockIcon },
  { id: 'grinding-noise', label: 'Making a grinding or humming noise', icon: VolumeUpIcon },
  { id: 'leaking', label: 'Leaking underneath', icon: WaterDropIcon },
  { id: 'not-draining', label: 'Not draining properly', icon: SpeedIcon },
  { id: 'bad-smell', label: 'Bad smell coming from disposal', icon: ReportProblemIcon },
  { id: 'not-sure', label: 'Not sure', icon: HelpOutlineIcon },
];

const DISPOSAL_AGE_OPTIONS = [
  { id: 'under-5', label: 'Less than 5 years', icon: NewReleasesIcon },
  { id: '5-to-10', label: '5–10 years', icon: TimerIcon },
  { id: 'over-10', label: 'Over 10 years', icon: HistoryIcon },
  { id: 'not-sure', label: "Not sure / don't know", icon: HelpOutlineIcon },
];

const DISPOSAL_TRIED_OPTIONS = [
  { id: 'reset-button', label: 'Yes, I tried the reset button', icon: RestartAltIcon },
  { id: 'allen-wrench', label: 'Yes, I tried unjamming it with an Allen wrench', icon: HandymanIcon },
  { id: 'tried-both', label: 'I tried both', icon: CheckIcon },
  { id: 'no-try', label: "No, I haven't tried anything", icon: SentimentNeutralIcon },
  { id: 'not-comfortable', label: "I'm not comfortable trying", icon: HelpOutlineIcon },
];

const URGENT_EMERGENCY_OPTIONS = [
  { id: 'flooding', label: "Water is flooding / won't stop", icon: FloodIcon, urgent: true },
  { id: 'burst-pipe', label: 'Burst or broken pipe', icon: WaterDamageIcon, urgent: true },
  { id: 'sewage-backup', label: 'Sewage backup', icon: WarningAmberIcon, urgent: true },
  { id: 'gas-smell', label: 'Gas leak smell near plumbing', icon: GasMeterIcon, urgent: true },
  { id: 'frozen-burst', label: 'Frozen pipe that burst', icon: SevereColdIcon, urgent: true },
  { id: 'toilet-overflow', label: "Toilet overflowing and won't stop", icon: WcIcon, urgent: true },
  { id: 'other', label: 'Other urgent issue', icon: ErrorOutlineIcon, urgent: true },
];

const URGENT_SHUTOFF_OPTIONS = [
  { id: 'yes-main', label: 'Yes, I turned off the main water', icon: CheckIcon },
  { id: 'yes-local', label: 'Yes, I turned off a local shutoff valve', icon: CheckIcon },
  { id: 'no-dont-know', label: "No, I don't know how", icon: HelpOutlineIcon },
  { id: 'no-cant-reach', label: "No, I can't reach it", icon: BlockIcon },
];

// ─── Flow branch definitions ────────────────────────────────────────────────
const FLOW_BRANCHES = {
  'clogged-drain': [
    { key: 'location', title: 'Where is the clog?', subtitle: 'This helps us match you with the right specialist.', icon: LocationOnIcon, options: DRAIN_LOCATION_OPTIONS },
    { key: 'symptom', title: "What's happening?", subtitle: 'Pick the description that fits best.', icon: WaterDropIcon, options: DRAIN_SYMPTOM_OPTIONS },
  ],
  'leaking-pipe': [
    { key: 'leakLocation', title: "What's leaking?", subtitle: 'Select where the leak is coming from.', icon: WaterDropIcon, options: LEAK_LOCATION_OPTIONS },
    { key: 'leakSeverity', title: 'How bad is the leak?', subtitle: 'This helps us gauge the urgency.', icon: OpacityIcon, options: LEAK_SEVERITY_OPTIONS },
    { key: 'leakShutoff', title: 'Have you shut off the water?', subtitle: 'This helps the plumber know what to expect.', icon: PlumbingIcon, options: LEAK_SHUTOFF_OPTIONS },
  ],
  'toilet-issue': [
    { key: 'toiletSymptom', title: "What's the toilet doing?", subtitle: 'Pick the description that fits best.', icon: WcIcon, options: TOILET_SYMPTOM_OPTIONS },
    { key: 'toiletCount', title: 'How many toilets are affected?', subtitle: 'This helps us understand the scope of the issue.', icon: FormatListNumberedIcon, options: TOILET_COUNT_OPTIONS },
    { key: 'toiletTried', title: 'Have you tried anything?', subtitle: "No wrong answer — this helps the plumber come prepared.", icon: HandymanIcon, options: TOILET_TRIED_OPTIONS },
  ],
  'water-heater': [
    { key: 'whNeed', title: 'What do you need?', subtitle: 'This helps us point you in the right direction.', icon: WhatshotIcon, options: WH_NEED_OPTIONS },
    { key: 'whRepairSymptom', title: "What's going on with your water heater?", subtitle: 'Pick the description that fits best.', icon: ThermostatIcon, options: WH_REPAIR_SYMPTOM_OPTIONS, condition: (fd) => fd.whNeed === 'repair' },
    { key: 'whType', title: 'What type of water heater?', subtitle: "If you're not sure, we can help you decide.", icon: WhatshotIcon, options: WH_TYPE_OPTIONS, condition: (fd) => fd.whNeed === 'install' },
    { key: 'whReason', title: "What's prompting this?", subtitle: 'This helps plumbers prepare the right quote.', icon: SearchIcon, options: WH_REASON_OPTIONS, condition: (fd) => fd.whNeed === 'install' },
  ],
  'faucet-install': [
    { key: 'faucetNeed', title: 'What do you need?', subtitle: 'Let us know what kind of work you need done.', icon: CountertopsIcon, options: FAUCET_NEED_OPTIONS },
    { key: 'faucetWhere', title: 'Where?', subtitle: 'This helps us match you with the right installer.', icon: LocationOnIcon, options: FAUCET_WHERE_OPTIONS },
    { key: 'faucetPurchased', title: 'Do you have the new fixture(s) already?', subtitle: 'This helps the pro know whether to bring supplies.', icon: ShoppingCartIcon, options: FAUCET_PURCHASED_OPTIONS },
  ],
  'garbage-disposal': [
    { key: 'disposalSymptom', title: "What's going on with the disposal?", subtitle: 'Pick the description that fits best.', icon: DeleteOutlineIcon, options: DISPOSAL_SYMPTOM_OPTIONS },
    { key: 'disposalAge', title: 'How old is the disposal?', subtitle: 'This helps the plumber decide between repair and replacement.', icon: HistoryIcon, options: DISPOSAL_AGE_OPTIONS },
    { key: 'disposalTried', title: 'Have you tried anything?', subtitle: "No wrong answer — this helps the plumber come prepared.", icon: HandymanIcon, options: DISPOSAL_TRIED_OPTIONS },
  ],
  'urgent-plumbing': [
    { key: 'urgentEmergency', title: "What's the emergency?", subtitle: "Tell us what's happening so we can act fast.", icon: ReportProblemIcon, options: URGENT_EMERGENCY_OPTIONS },
    { key: 'urgentShutoff', title: 'Have you shut off the water?', subtitle: 'If you can safely do so, this can prevent further damage.', icon: PlumbingIcon, options: URGENT_SHUTOFF_OPTIONS },
  ],
};

const SHARED_TAIL_KEYS = ['urgency', 'photos', 'address', 'review'];
const URGENT_TAIL_KEYS = ['photos', 'address', 'review'];

const STEP_LABELS = {
  problem: {
    title: "What's going on with your plumbing?",
    subtitle: 'Select the issue that best describes what you need help with.',
    icon: PlumbingIcon,
  },
  urgency: {
    title: 'When do you need help?',
    subtitle: "Let us know your timeline so we can prioritize.",
    icon: ScheduleIcon,
  },
  photos: {
    title: 'Photos & details',
    subtitle: 'Optional — but photos help plumbers give better estimates.',
    icon: AddPhotoAlternateIcon,
  },
  address: {
    title: 'What city do you live in?',
    subtitle:
      'Enter your city and state so we can match you with nearby pros. No street address needed.',
    icon: HomeIcon,
  },
  review: {
    title: 'Review your request',
    subtitle: "Make sure everything looks good before we find your plumber.",
    icon: CheckCircleOutlineIcon,
  },
};

const ILLUSTRATION_CAPTIONS = {
  problem: "Tell us what's going on and we'll connect you with the right local plumber.",
  location: 'Knowing the location helps us match the right specialist for the job.',
  symptom: 'The more detail you provide, the more accurate your quotes will be.',
  toiletSymptom: 'Different toilet problems need different fixes — this helps us narrow it down.',
  toiletCount: 'If multiple toilets are affected, it may point to a larger issue.',
  toiletTried: "No judgment — this helps the plumber come prepared with the right parts.",
  leakLocation: 'Where the leak is tells us a lot about what kind of fix is needed.',
  leakSeverity: 'A slow drip and a gushing pipe need very different responses.',
  leakShutoff: 'If you can safely shut off the water, it can prevent further damage.',
  faucetNeed: "Install or replace — we'll match you with an experienced installer.",
  faucetWhere: 'Different rooms can mean different plumbing requirements.',
  faucetPurchased: 'If you already have your fixtures, the pro just needs to install them.',
  disposalSymptom: 'Knowing the symptom helps the plumber bring the right tools and parts.',
  disposalAge: 'Older disposals may be more cost-effective to replace than repair.',
  disposalTried: "A quick reset sometimes does the trick — but if not, we've got you covered.",
  urgentEmergency: "We're prioritizing your request — hang tight.",
  urgentShutoff: 'Shutting off the water can prevent thousands in damage.',
  whNeed: "Whether it's a repair or a new install, we'll match you with the right pro.",
  whRepairSymptom: 'These details help the plumber diagnose the issue before they arrive.',
  whType: "Not sure which type is best? Your plumber can help you decide.",
  whReason: 'Understanding the why helps plumbers prepare the most accurate quote.',
  urgency: 'We prioritize urgent requests and match availability to your timeline.',
  photos: 'A picture is worth a thousand words — especially for plumbing.',
  address: "We'll use your city and state to find plumbers who service your area.",
  review: "Almost there! Review your info and we'll find the best local pros.",
};

/** Plumbing question config (saved-request display; see wizardServiceRegistry) */
export const plumbingSubserviceFlowConfig = {
  problemStep: {
    options: PLUMBING_PROBLEMS,
    ...STEP_LABELS.problem,
  },
  flowBranches: FLOW_BRANCHES,
  sharedTailKeys: SHARED_TAIL_KEYS,
  urgentTailKeys: URGENT_TAIL_KEYS,
  urgentProblemId: 'urgent-plumbing',
  urgencyStep: {
    options: URGENCY_OPTIONS,
    ...STEP_LABELS.urgency,
  },
  photosStep: STEP_LABELS.photos,
  addressStep: STEP_LABELS.address,
  reviewStep: STEP_LABELS.review,
  illustrationCaptions: ILLUSTRATION_CAPTIONS,
  submitButtonLabel: 'Find my plumber',
};